import {Button, Col, Form, Row, Spinner} from 'react-bootstrap'
import {TFormAllValues, TFormKeys} from '../policiesTypes'
import {IResZipCode} from '../../../types/quoteTypes'
import {FormikErrors, FormikTouched} from 'formik'
import {
  GooglePlacesAutocompleteInput,
  AddressComponent,
  PlaceSelection,
} from 'app/tfi/components/GooglePlacesAutocompleteInput'

export const AddressInfoTab: React.FC<{
  onPrev: () => void
  onNext: () => void
  handleZipCodeLookup: () => void
  loadingZipCode: boolean
  dataZipCode?: IResZipCode
  handleChange: (field: string, value: any) => void
  handleBlur: (field: string) => void
  values: TFormAllValues
  errors: FormikErrors<TFormAllValues>
  touched: FormikTouched<TFormAllValues>
  isNextSubmit?: boolean
}> = ({
  onNext,
  onPrev,
  handleZipCodeLookup,
  loadingZipCode,
  dataZipCode,
  handleBlur,
  handleChange,
  values,
  errors,
  touched,
  isNextSubmit,
}) => {
  const getPart = (components: AddressComponent[] | undefined, ...types: string[]) => {
    if (!components) return ''
    const component = components.find((item) => types.every((type) => item.types.includes(type)))
    return component?.long_name ?? ''
  }

  const getShortPart = (components: AddressComponent[] | undefined, ...types: string[]) => {
    if (!components) return ''
    const component = components.find((item) => types.every((type) => item.types.includes(type)))
    return component?.short_name ?? ''
  }

  const buildStreetAddress = (components: AddressComponent[] | undefined, fallback?: string) => {
    if (!components || components.length === 0) return fallback ?? ''
    const streetNumber = getShortPart(components, 'street_number')
    const route = getPart(components, 'route')
    const streetAddress = [streetNumber, route].filter(Boolean).join(' ').trim()
    return streetAddress || fallback || ''
  }

  const handleAddressSelect = ({address, addressComponents}: PlaceSelection) => {
    const streetAddress = buildStreetAddress(addressComponents, address)
    if (streetAddress) {
      handleChange('address1', streetAddress)
    }
    if (!addressComponents) return

    const city =
      getPart(addressComponents, 'locality') ||
      getPart(addressComponents, 'postal_town') ||
      getPart(addressComponents, 'administrative_area_level_2')
    const stateLong = getPart(addressComponents, 'administrative_area_level_1')
    const stateShort = getShortPart(addressComponents, 'administrative_area_level_1')
    const postalCode = getPart(addressComponents, 'postal_code')

    if (city) handleChange('city', city)
    if (postalCode) handleChange('zipCode', postalCode)
    if (stateLong || stateShort) {
      handleChange('stateEU', {code: stateShort || stateLong, name: stateLong || stateShort})
    }
  }

  return (
    <>
      <div className='address-contact-information'>
        <div className='title-section-policy-form'>
          <h6>Address information</h6>
        </div>
        <Row className='address-section-policy-form'>
          <Col lg={6} md={6} sm={12} xs={12}>
            <Form.Group>
              <Form.Label>Address Line 1</Form.Label>
              {/* <Form.Control
                placeholder='Enter address'
                onChange={(e) => handleChange('address1', e.target.value)}
                onBlur={() => handleBlur('address1')}
                value={values.address1}
                required
              /> */}
              <GooglePlacesAutocompleteInput
                placeholder='Enter address line 1'
                value={values.address1}
                onChange={(e) => handleChange('address1', e.target.value)}
                onBlur={() => handleBlur('address1')}
                onPlaceSelect={handleAddressSelect}
                name='address1'
              />
            </Form.Group>
          </Col>
          <Col lg={6} md={6} sm={12} xs={12}>
            <Form.Group>
              <Form.Label>Address Line 2</Form.Label>
              <Form.Control
                placeholder='Enter address line 2'
                onChange={(e) => handleChange('address2', e.target.value)}
                onBlur={() => handleBlur('address2')}
                value={values.address2}
                required
              />
            </Form.Group>
          </Col>
          <Col lg={3} md={6} sm={12} xs={12}>
            <Form.Group>
              <Form.Label>
                Zip Code <small className='text-danger'>{errors.zipCode}</small>
              </Form.Label>
              <Form.Control
                placeholder='Enter zip code'
                type='text'
                name='zipCode'
                value={values.zipCode}
                onChange={(e) => handleChange('zipCode', e.target.value)}
                onBlur={handleZipCodeLookup}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault() // Evitar submit
                    handleZipCodeLookup()
                  }
                }}
              />
            </Form.Group>
          </Col>
          <Col lg={3} md={6} sm={12} xs={12}>
            <Form.Group>
              <Form.Label>State</Form.Label>
              <div className='address-state'>
                <Form.Control
                  type='text'
                  className='state-full'
                  placeholder='State'
                  value={
                    values.stateEU?.code && values.stateEU?.name
                      ? `${values.stateEU.code} - ${values.stateEU.name}`
                      : ''
                  }
                  readOnly
                />

                {/* <Form.Control
                  type='hidden'
                  name='stateEU'
                  value={values.stateEU.code}
                  onChange={(e) => handleChange('stateEU.code', values.stateEU.code)}
                />
                <Form.Control
                  type='hidden'
                  name='stateProvince'
                  value={values.stateEU.name}
                  onChange={(e) =>
                    handleChange('stateEU.name', values.stateEU.name)
                  }
                /> */}
              </div>
            </Form.Group>
          </Col>
          <Col lg={6} md={6} sm={12} xs={12}>
            <Form.Group>
              <Form.Label>County FIPS</Form.Label>{' '}
              {loadingZipCode && (
                <span>
                  <Spinner animation='border' size='sm' />
                </span>
              )}
              <Form.Select
                value={values.countyFips}
                onChange={(e) => handleChange('countyFips', e.target.value)}
              >
                <option value=''>Select county FIPS</option>
                {dataZipCode?.zipcodeByZip?.countyNamesAll?.split('|').map((name, idx) => {
                  const fips = dataZipCode.zipcodeByZip?.countyFipsAll?.split('|')[idx]
                  return (
                    <option key={fips} value={fips}>
                      {name} - {fips}
                    </option>
                  )
                })}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      </div>
      <div className='btn-prev-next'>
        <Button size='sm' variant='outline' onClick={onPrev}>
          PREVIOUS
        </Button>
        {isNextSubmit ? (
          <Button size='sm' variant='info' onClick={onNext}>
            Save
          </Button>
        ) : (
          <Button size='sm' onClick={onNext}>
            NEXT STEP
          </Button>
        )}
      </div>
    </>
  )
}
